import { createContext, useEffect, useReducer, useState } from 'react';

// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { Loader } from '@crehana/ui';
import { notEmpty } from '@crehana/utils';
import { useMe } from '@crehana/web/ReactContexts/AuthContext';
import { useRouter } from 'next/router';

import { MAX_LICENSES } from '../constants';
import usePricingTeamPlan from '../hooks/usePricingTeamPlan';
import {
  ACTIONS,
  type ActionType,
  type CheckoutContextValues,
  type InitialStateType,
} from './types';

const initialState: InitialStateType = {
  currentStep: 1,
  licences: 1,
  accountDetails: null,
  totalPrice: 0,
};

export const CheckoutContext = createContext<CheckoutContextValues>({
  state: initialState,
  dispatch: () => undefined,
  ONBOARDING_STEPS: [],
  pricePerSeat: 0,
  loading: false,
  currency: '',
  productName: '',
  productId: '',
});

export function reducer(
  state: InitialStateType,
  action: ActionType,
): InitialStateType {
  switch (action.type) {
    case ACTIONS.NAVIGATE:
      return {
        ...state,
        currentStep: action.payload,
      };
    case ACTIONS.SET_LICENCE_NUMBER:
      return {
        ...state,
        licences: action.payload,
      };
    case ACTIONS.SET_ACCOUNT_DETAILS:
      return {
        ...state,
        accountDetails: action.payload,
      };
    case ACTIONS.SET_TOTAL_PRICE:
      return {
        ...state,
        totalPrice: action.payload,
      };
    default:
      return state;
  }
}

export const CheckoutProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const router = useRouter();
  const [isReady, setIsReady] = useState(false);
  const { licences, productId } = router.query as {
    licences: string;
    productId: string;
  };
  const { me } = useMe();

  const ONBOARDING_STEPS = [
    me
      ? null
      : {
          id: 1,
          label: 'Regístrate',
        },
    {
      id: 2,
      label: 'Detalle de Cuenta',
    },
    {
      id: 3,
      label: 'Proceso de Pago',
    },
  ].filter(notEmpty);

  useEffect(() => {
    const numberLicences = parseInt(licences, 10);

    if (router.isReady) {
      if (licences && numberLicences > 0 && numberLicences <= MAX_LICENSES) {
        dispatch({
          type: ACTIONS.SET_LICENCE_NUMBER,
          payload: parseInt(licences, 10),
        });
      } else {
        window.location.href = '/business/precios/';
      }
      if (me) {
        dispatch({
          type: ACTIONS.SET_ACCOUNT_DETAILS,
          payload: {
            firstName: me.firstName || '',
            lastName: me.lastName || '',
            email: me.email || '',
            role: '',
            company: '',
            phoneNumber: {
              code: me.phoneCode || '',
              phoneNumber: me.phoneNumber || '',
            },
            country: undefined,
            docNroCompany: '',
          },
        });
        dispatch({
          type: ACTIONS.NAVIGATE,
          payload: 2,
        });
      }
    }
  }, [licences, me, router]);

  const {
    pricePerSeat,
    loading,
    currency,
    productName,
    productId: pricingProductId,
  } = usePricingTeamPlan({
    seats: state.licences,
    ID: productId,
    dispatch,
  });

  const value: CheckoutContextValues = {
    state,
    dispatch,
    ONBOARDING_STEPS,
    pricePerSeat,
    loading,
    currency,
    productName,
    productId: pricingProductId,
  };

  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: legacy code - style/useBlockStatements
    if (router.isReady && !loading) setIsReady(true);
  }, [router.isReady, loading]);

  // biome-ignore lint/style/useBlockStatements: legacy code - style/useBlockStatements
  if (!isReady)
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <Loader />
      </div>
    );

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};
